<!-- KpiRow.vue -->
<template>
  <div class="kpi-row" :class="gridClass">
    <div class="kpi-row__label">
      <span>{{ kpi.label }}</span>
      <div v-if="trend" class="kpi-row__trend">
        <ArrowUp v-if="trend === 'up'" :size="16" class="kpi-row__trend-icon--up" />
        <ArrowDown v-else-if="trend === 'down'" :size="16" class="kpi-row__trend-icon--down" />
        <Minus v-else :size="16" class="kpi-row__trend-icon--neutral" />
      </div>
    </div>
    <div
      v-for="(wave, index) in waves"
      :key="index"
      class="kpi-row__value"
      :class="[
        `kpi-row__value--${getStatus(wave.Name)}`,
        { 'kpi-row__value--current': wave.isCurrent }
      ]"
    >
      {{ getValue(wave.Name) }}
    </div>
  </div>
</template>

<script>
import { ArrowUp, ArrowDown, Minus } from 'lucide-vue-next';

export default {
  name: 'KpiRow',

  components: {
    ArrowUp,
    ArrowDown,
    Minus
  },

  props: {
    trend: {
      type: String,
      default: null,
      validator: (val) => [null, 'up', 'down', 'neutral'].includes(val)
    },
    column: {
      type: Object,
      required: true
    },
    kpi: {
      type: Object,
      required: true
    },
  },

  computed: {
    waves() {
      return this.column.isExpanded
        ? this.column.waves
        : this.column.waves.slice(-1);
    },
    gridClass() {
      if (this.waves?.length > 1) {
        return 'kpi-row--multi';
      }
      return 'kpi-row--single';
    }
  },

  methods: {
    getValue(waveName) {
      const wave = this.kpi.SurveyWave.find(w => w.Name === waveName);
      return wave ? wave.Result : '-';
    },
    getStatus(waveName) {
      const wave = this.kpi.SurveyWave.find(w => w.Name === waveName);
      if (!wave) return null;

      if (wave.Result <= 50) return 'red';
      if (wave.Result <= 75) return 'yellow';
      return 'green';
    }
  }
};
</script>

<style lang="scss" scoped>
.kpi-row {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.625rem 0.5rem;
  border-bottom: 1px solid var(--bs-border-color);
  transition: background-color 0.15s;

  &:hover {
    background-color: rgba(var(--bs-gray-100-rgb), 0.5);
  }
  &--single {
    display: grid;
    grid-template-columns: minmax(8rem, 1fr) repeat(1, 4rem);
    gap: 0.125rem;
  }

  &--multi {
    display: grid;
    grid-template-columns: minmax(8rem, 1fr) repeat(v-bind('waves.length'), 4rem);
    gap: 0.125rem;
  }
}

.kpi-row__label {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 12px;
  color: var(--bs-gray-700);
  max-width: 8rem;

  span {
    flex: 1;
  }
}

.kpi-row__trend {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  margin-left: auto;
}

.kpi-row__trend-icon {
  &--up {
    color: var(--bs-success);
  }

  &--down {
    color: var(--bs-danger);
  }

  &--neutral {
    color: var(--bs-gray-400);
  }
}

.kpi-row__value {
  text-align: center;
  font-size: 0.875rem;
  border-radius: var(--bs-border-radius);
  padding: 0.125rem 0.25rem;

  &--current {
    font-weight: 700;
  }

  &--green {
    border-left: 4px solid var(--bs-success);
    background-color: rgba(var(--bs-success-rgb), 0.1);
  }

  &--yellow {
    border-left: 4px solid var(--bs-warning);
    background-color: rgba(var(--bs-warning-rgb), 0.1);
  }

  &--red {
    border-left: 4px solid var(--bs-danger);
    background-color: rgba(var(--bs-danger-rgb), 0.1);
  }
}
</style>
